import React from "react";
import SocialsList from "../../components/SocialsList/SocialsList";
import NcImage from "../../components/NcImage/NcImage";
import { GlobeAltIcon, ArrowDownTrayIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import VerifyIcon from "../../components/VerifyIcon";
import Image from "../../components/Image";

const PageAuthor = () => {
  // ===== DATA =====
  const summary = [
    "AI Architect & Software Engineering Manager with 13+ years across Full-Stack, Cloud, and Generative AI.",
    "Specialized in RAG, agentic frameworks (CrewAI, LangChain Agents), and production ML with vLLM/KServe.",
    "Hands-on with Java/Spring Boot, React, Kafka; CI/CD and observability with Docker/Kubernetes/MLflow.",
    "Delivered impact: −60% deploy time, +30% throughput, −25% AI compute cost.",
  ];

  const skills = [
    {
      title: "Full-Stack",
      items: ["Java", "Python", "Spring Boot", "React", "Kafka", "MySQL", "MongoDB", "Redis", "Microservices"],
    },
    {
      title: "GenAI / MLOps",
      items: [
        "LLMs (OpenAI, Claude, Gemini, Mistral, Llama)",
        "RAG (LangChain, LlamaIndex)",
        "Embeddings (MixedBread, OpenAI)",
        "Fine-tuning (LoRA/QLoRA/PEFT)",
        "vLLM, MLflow, Airflow",
      ],
    },
    {
      title: "Cloud & Orchestration",
      items: [
        "AWS (S3, EC2, Lambda, SageMaker, Bedrock, CloudFormation)",
        "GCP (Vertex AI)",
        "KServe, MCP",
        "EKS/OCP/Kubernetes",
        "Cost Optimization",
      ],
    },
    {
      title: "DevSecOps & Observability",
      items: ["Jenkins", "Terraform", "Nexus IQ", "SonarQube", "Sealights", "Docker", "Datadog", "Prometheus", "Grafana", "ELK"],
    },
    { title: "Testing", items: ["JUnit", "TestNG", "Mockito", "Selenium", "RestAssured", "Playwright", "JMeter"] },
  ];

  const experienceHighlights = [
    {
      role: "Technology Manager — Discover Financial Services (IL, USA)",
      time: "Aug 2023 – Present",
      bullets: [
        "Deployed vLLM endpoints with KServe autoscaling → −30% GPU utilization.",
        "Built Test Case Analyzer RAG framework using MixedBread AI, PgVector, Fine-tuning LLM using QLoRA for domain specfic tasks.",
        "Datadog for monitoring-as-code; optimized OCP/K8s rollouts & HPA.",
        "Improved React/Spring Boot performance; Kafka + reactive pipelines for high throughput.",
      ],
    },
    {
      role: "Full-Stack Developer — Susash Consulting (USA)",
      time: "Aug 2022 – Aug 2023",
      bullets: [
        "Modernized legacy portal to microservices on AWS → +45% deploy velocity, +30% uptime.",
        "Orchestrated ML with Airflow → 80% ETL effort reduction; automated retrains on SageMaker/Vertex.",
        "Built RAG prototype (LangChain + HF) on Lambda/FastAPI → ~80% response accuracy.",
      ],
    },
    {
      role: "Senior Technical Lead — Mercedes-Benz R&D (India)",
      time: "Apr 2018 – Jul 2022",
      bullets: [
        "XGBoost/SVM forecasting → +25% resource-planning efficiency; −20% latency with Kafka/WebFlux.",
        "ELK monitoring → −30% downtime/MTTR; CI/CD with Jenkins across Dev/INT/Prod.",
        "Security with Spring Security (OAuth2/OpenID), AOP, JPA; Docker/K8s on EC2 with HPA.",
      ],
    },
    {
      role: "Senior Software Engineer — Accenture (India) · Client: BestBuy",
      time: "May 2012 – Mar 2018",
      bullets: [
        "Designed Java backend workflows (Spring/Hibernate) and secure auth; −30% defects via test automation.",
        "Optimized delivery with Maven/Tomcat, SonarQube; promoted for complex, zero-issue releases.",
      ],
    },
  ];

  const projects = [
    {
      name: "AI Study Partner — RAG Research Assistant",
      detail: "LangChain + LlamaIndex + FAISS; adaptive chunking; FastAPI on Lambda; <5% hallucination, ~3× faster reading.",
    },
    {
      name: "Chat Summarizer — Gemini Flash",
      detail: "Condenses 150+ messages into structured insights; GCF + Firestore; −75% manual note-taking.",
    },
    {
      name: "AI Wellness Planner — Multi-Agent",
      detail: "CrewAI + LangChain Agents + LlamaAgent; Redis memory; −35% hallucinations, +25% relevance.",
    },
    {
      name: "Multi-LLM Orchestration — MCP",
      detail: "Vendor-agnostic routing across OpenAI/Gemini/Claude/Mistral; gRPC; +35% throughput, −25% cost.",
    },
  ];

  const certsAwards = [
    "AWS Solutions Architect — Associate (valid thru 02/2028)",
    "Accenture Centre of Excellence Award (2017)",
    "Mercedes-Benz ‘More’ Award (2020)",
  ];

  const links = [
    { label: "GitHub", href: "https://github.com/shilpathota" },
    { label: "LinkedIn", href: "https://www.linkedin.com/in/shilpa-thota/" },
    { label: "Blog", href: "https://blog.shilpathota.com/" },
  ];

  const tagsFlat = [
    "Kubernetes", "Docker", "AWS", "GCP", "Terraform",
    "Java", "Spring Boot", "React", "Python",
    "Microservices", "Kafka", "Redis", "MySQL/MongoDB",
    "CI/CD", "DevSecOps", "Observability",
    "AI/ML", "MLOps", "LLMOps",
  ];

  return (
    <div className="nc-PageAuthor">
      {/* ===== HERO ===== */}
      <div className="relative w-full h-40 md:h-60 2xl:h-72">
        <NcImage
          alt="Code and cloud-native themed header"
          containerClassName="absolute inset-0"
          sizes="(max-width: 1280px) 100vw, 1536px"
          src="https://www.zdnet.com/a/img/resize/0a6b0be2f543ddbf313fc83a706b807b77c3c202/2021/07/19/8a337c80-5ed6-43a1-98fb-b981d420890f/programming-languages-shutterstock-1680857539.jpg?auto=webp&fit=crop&height=900&width=1200"
          className="object-cover w-full h-full"
          fill
        />
      </div>

      {/* ===== CARD ===== */}
      <div className="container -mt-10 lg:-mt-16">
        <div className="relative bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 p-5 lg:p-8 rounded-3xl md:rounded-[40px] shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-4 xl:col-span-3">
            {/* Avatar + Name */}
            <div className="flex items-center gap-4">
              <div className="wil-avatar relative flex-shrink-0 inline-flex items-center justify-center overflow-hidden rounded-full w-20 h-20 lg:w-24 lg:h-24 ring-4 ring-white dark:ring-0 shadow-2xl">
                <Image alt="Portrait of Shilpa Thota" src="/shilpa.jpg" fill className="object-cover" />
              </div>
              <div>
                <h2 className="flex items-center text-2xl font-semibold tracking-tight">
                  <span>Shilpa Thota</span>
                  <VerifyIcon className="ml-2" iconClass="w-6 h-6" />
                </h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  Technology Leader · Cloud & AI
                </p>
              </div>
            </div>

            {/* Links */}
            <div className="mt-6 space-y-3">
              <a
                href="https://shilpathota.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-200"
              >
                <GlobeAltIcon className="w-5 h-5" />
                shilpathota.com
              </a>
              <a
                href="mailto:shilpathota1408@gmail.com"
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-200"
              >
                <EnvelopeIcon className="w-5 h-5" />
                shilpathota1408@gmail.com
              </a>


              <SocialsList itemClass="block w-7 h-7" />

              <a
                href="/Shilpa_Thota_Resume.pdf"
                className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100 hover:underline"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
                Download Resume (PDF)
              </a>
            </div>

            {/* Quick skills chips */}
            <ul className="flex flex-wrap gap-2 mt-6">
              {tagsFlat.map((t) => (
                <li
                  key={t}
                  className="px-2.5 py-1 rounded-full text-xs border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200"
                >
                  {t}
                </li>
              ))}
            </ul>
          </aside>

          {/* Main */}
          <main className="lg:col-span-8 xl:col-span-9 space-y-10">
            {/* Summary */}
            <section>
              <h3 className="text-xl font-semibold mb-3">Summary</h3>
              <ul className="list-disc pl-5 space-y-2 text-neutral-700 dark:text-neutral-200">
                {summary.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </section>

            {/* Skills */}
            <section>
              <h3 className="text-xl font-semibold mb-4">Core Skills</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {skills.map((group) => (
                  <div key={group.title} className="p-4 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800">
                    <h4 className="font-semibold mb-2">{group.title}</h4>
                    <div className="flex flex-wrap gap-2">
                      {group.items.map((it) => (
                        <span key={it} className="text-xs px-2 py-1 rounded-md bg-white/60 dark:bg-black/30 border border-neutral-200 dark:border-neutral-700">
                          {it}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Experience Highlights */}
            <section>
              <h3 className="text-xl font-semibold mb-4">Experience Highlights</h3>
              <div className="space-y-6">
                {experienceHighlights.map((exp) => (
                  <div key={exp.role} className="border border-neutral-200 dark:border-neutral-700 rounded-2xl p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="font-semibold">{exp.role}</h4>
                      <span className="text-sm text-neutral-500">{exp.time}</span>
                    </div>
                    <ul className="list-disc pl-5 mt-2 space-y-1 text-neutral-700 dark:text-neutral-200">
                      {exp.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Projects */}
            <section>
              <h3 className="text-xl font-semibold mb-4">Glimpse of Projects</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {projects.map((p) => (
                  <div key={p.name} className="rounded-2xl border border-neutral-200 dark:border-neutral-700 p-4">
                    <h4 className="font-semibold">{p.name}</h4>
                    <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-200">{p.detail}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Certifications & Awards */}
            <section>
              <h3 className="text-xl font-semibold mb-3">Certifications & Awards</h3>
              <ul className="list-disc pl-5 space-y-1 text-neutral-700 dark:text-neutral-200">
                {certsAwards.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </section>

            {/* Links */}
            <section>
              <h3 className="text-xl font-semibold mb-3">Links</h3>
              <div className="flex flex-wrap gap-3">
                {links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium px-3 py-1 rounded-full border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PageAuthor;
